/**
 * Seating Map - Suzanne Lenglen
 * Interactive SVG seating chart for French Open Lenglen court tickets
 *
 * Categories:
 * - CAT 1: Inner ring, rows 12-18 closest to court (gold)
 * - CAT 2: Outer ring (coral/orange)
 */

(function() {
  'use strict';

  // Helper function to format prices in USD or EUR
  function formatMapPrice(dollars) {
    if (window.CurrencyConverter && window.CurrencyConverter.isEurMode()) {
      return window.CurrencyConverter.convertDollars(dollars);
    }
    return '$' + Math.round(dollars).toLocaleString('en-US');
  }

  const CATEGORIES = {
    'cat-1': {
      name: 'Category 1',
      description: 'Inner ring seating, rows 12-18. Closest to the court with excellent views.',
      color: '#e8c858',
      searchTerms: ['cat 1 ', 'cat 1\n', 'category 1']
    },
    'cat-2': {
      name: 'Category 2',
      description: 'Outer ring seating. Great views with value pricing.',
      color: '#FF6B35',
      searchTerms: ['cat 2', 'category 2']
    }
  };

  const TRIBUNE_NAMES = {
    'north': 'Tribune North',
    'south': 'Tribune South',
    'east': 'Tribune East',
    'west': 'Tribune West'
  };

  class SeatingMapLenglen {
    constructor() {
      this.map = document.getElementById('lenglen-map');
      this.tooltip = document.getElementById('map-tooltip');
      this.ticketsPanel = document.getElementById('seating-map-tickets');
      this.sections = null;
      this.legendItems = null;
      this.activeCategory = null;
      this.zoomLevel = 1;
      this.maxZoom = 2;
      this.minZoom = 0.8;

      if (!this.map) return;

      this.init();
    }

    init() {
      this.sections = this.map.querySelectorAll('.seat-section');
      this.legendItems = document.querySelectorAll('.legend-item');
      this.priceRanges = {};

      this.bindEvents();
      this.initZoomControls();
      this.calculatePriceRanges();
      this.markSoldOutSections();
      this.renderPriceLabels();
      this.hideSoldOutCategories();
      this.showAllTickets();
      this.setupSortControls();
      this.hideDuplicateTicketSection();

      // Currency converter: re-render price labels when EUR rate is ready
      document.addEventListener('currency:ready', () => {
        const oldGroup = document.getElementById('price-labels-group');
        if (oldGroup) oldGroup.remove();
        this.renderPriceLabels();
      });
    }

    calculatePriceRanges() {
      const ticketCards = this.getOriginalTicketCards();

      Object.keys(CATEGORIES).forEach(category => {
        this.priceRanges[category] = { min: Infinity, max: -Infinity, count: 0 };
      });

      ticketCards.forEach(card => {
        const cardText = card.textContent;
        const category = this.detectCardCategory(cardText);
        if (!category) return;

        const priceEl = card.querySelector('.price-value');
        if (priceEl) {
          const priceText = priceEl.textContent.replace(/[^0-9.,]/g, '').replace(',', '');
          const price = parseFloat(priceText);
          if (!isNaN(price)) {
            this.priceRanges[category].min = Math.min(this.priceRanges[category].min, price);
            this.priceRanges[category].max = Math.max(this.priceRanges[category].max, price);
            this.priceRanges[category].count++;
          }
        }
      });
    }

    // Grey out map sections whose category has zero available listings
    markSoldOutSections() {
      if (!this.sections) return;
      this.sections.forEach(section => {
        const cat = section.dataset.category;
        const pr = this.priceRanges[cat];
        const soldOut = !pr || pr.count === 0;
        section.classList.toggle('smux-soldout', soldOut);
      });
    }

    hideSoldOutCategories() {
      const ticketCards = this.getOriginalTicketCards();
      const categoriesWithTickets = new Set();

      ticketCards.forEach(card => {
        const cardText = card.textContent;
        const category = this.detectCardCategory(cardText);
        if (category) {
          categoriesWithTickets.add(category);
        }
      });

      this.legendItems.forEach(item => {
        const category = item.dataset.category;
        if (!categoriesWithTickets.has(category)) {
          item.style.display = 'none';
        } else {
          this.addPriceToLegendItem(item, category);
        }
      });
    }

    addPriceToLegendItem(item, category) {
      const priceRange = this.priceRanges[category];
      if (!priceRange || priceRange.count === 0) return;

      if (item.querySelector('.legend-price')) return;

      const priceEl = document.createElement('span');
      priceEl.className = 'legend-price';

      if (priceRange.min === priceRange.max) {
        priceEl.textContent = formatMapPrice(priceRange.min);
      } else {
        priceEl.textContent = formatMapPrice(priceRange.min) + '+';
      }

      item.appendChild(priceEl);
    }

    hideDuplicateTicketSection() {
      const duplicateSection = document.querySelector('.ticket-selection-wrapper');
      if (duplicateSection) {
        duplicateSection.style.display = 'none';
      }
      const marketplaceSection = document.querySelector('.ticket-variants-marketplace-section');
      if (marketplaceSection) {
        marketplaceSection.style.display = 'none';
      }
    }

    bindEvents() {
      this.sections.forEach(section => {
        section.addEventListener('mouseenter', (e) => this.handleSectionHover(e, section));
        section.addEventListener('mouseleave', () => this.hideTooltip());
        section.addEventListener('click', () => this.handleSectionClick(section));
      });

      this.legendItems.forEach(item => {
        item.addEventListener('click', () => {
          const category = item.dataset.category;
          this.selectCategory(category);
        });
      });

      // Exclude the listings sidebar + sticky bar — clicking a card, accordion
      // header, qty chip or sort select must NOT reset the map filter
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.seating-map-wrapper') &&
            !e.target.closest('.seating-map-right') &&
            !e.target.closest('.smux-sticky') &&
            !e.target.closest('.legend-item') &&
            !e.target.closest('.category-info-panel')) {
          this.clearSelection();
        }
      });
    }

    initZoomControls() {
      const zoomIn = document.getElementById('map-zoom-in');
      const zoomOut = document.getElementById('map-zoom-out');
      const reset = document.getElementById('map-reset');

      if (zoomIn) {
        zoomIn.addEventListener('click', () => this.zoom(0.2));
      }
      if (zoomOut) {
        zoomOut.addEventListener('click', () => this.zoom(-0.2));
      }
      if (reset) {
        reset.addEventListener('click', () => this.resetZoom());
      }
    }

    zoom(delta) {
      this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel + delta));
      this.map.style.transform = `scale(${this.zoomLevel})`;
    }

    resetZoom() {
      this.zoomLevel = 1;
      this.map.style.transform = 'scale(1)';
    }

    handleSectionHover(e, section) {
      const category = section.dataset.category;
      const blockId = section.dataset.block;

      let categoryName = CATEGORIES[category]?.name || category;
      let tooltipText = blockId
        ? `${categoryName} block ${blockId}`
        : categoryName;

      // Consistent tooltip: section \u00b7 from $X \u00b7 N listings (or Sold out)
      const priceRange = this.priceRanges[category];
      if (priceRange && priceRange.count > 0) {
        const listingWord = priceRange.count === 1 ? 'listing' : 'listings';
        tooltipText += ` \u2022 from ${formatMapPrice(priceRange.min)} \u2022 ${priceRange.count} ${listingWord}`;
      } else {
        tooltipText += ' \u2022 Sold out';
      }

      this.showTooltip(e, tooltipText);
    }

    showTooltip(e, text) {
      if (!this.tooltip) return;

      this.tooltip.textContent = text;
      this.tooltip.classList.add('visible');

      const rect = this.map.parentElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top - 40;

      this.tooltip.style.left = `${x}px`;
      this.tooltip.style.top = `${y}px`;
      this.tooltip.style.transform = 'translateX(-50%)';
    }

    hideTooltip() {
      if (this.tooltip) {
        this.tooltip.classList.remove('visible');
      }
    }

    handleSectionClick(section) {
      const category = section.dataset.category;
      this.selectCategory(category);
    }

    selectCategory(category) {
      this.activeCategory = category;

      this.sections.forEach(section => {
        const sectionCategory = section.dataset.category;
        section.classList.remove('active', 'dimmed');

        if (sectionCategory === category) {
          section.classList.add('active');
        } else {
          section.classList.add('dimmed');
        }
      });

      this.legendItems.forEach(item => {
        item.classList.toggle('active', item.dataset.category === category);
      });

      this.showCategoryTickets(category);
      this.highlightTickets(category);
    }

    clearSelection() {
      this.activeCategory = null;

      this.sections.forEach(section => {
        section.classList.remove('active', 'dimmed');
      });

      this.legendItems.forEach(item => {
        item.classList.remove('active');
      });

      const ticketCards = document.querySelectorAll('.ticket-variant-card');
      ticketCards.forEach(card => {
        card.classList.remove('highlight', 'dimmed');
      });

      this.updatePriceLabelVisibility(null);
      this.showAllTickets();
    }

    highlightTickets(category) {
      const categoryData = CATEGORIES[category];
      if (!categoryData) return;

      const ticketCards = document.querySelectorAll('.ticket-variant-card');

      ticketCards.forEach(card => {
        const cardText = card.textContent.toLowerCase();
        let categoryMatch = this.matchCategory(cardText, category);

        if (categoryMatch) {
          card.classList.add('highlight');
          card.classList.remove('dimmed');
        } else {
          card.classList.remove('highlight');
          card.classList.add('dimmed');
        }
      });
    }

    matchCategory(cardText, category) {
      const categoryData = CATEGORIES[category];
      if (!categoryData) return false;

      if (category === 'cat-1') {
        return /cat\s*1(?:\s|$|row)/i.test(cardText) && !/cat\s*1\d/i.test(cardText);
      } else {
        return categoryData.searchTerms.some(term => cardText.includes(term.toLowerCase()));
      }
    }

    getOriginalTicketCards() {
      const allCards = document.querySelectorAll('.ticket-variant-card');
      return Array.from(allCards).filter(card => !card.closest('#seating-map-tickets'));
    }

    detectCardCategory(cardText) {
      const text = cardText.toLowerCase();

      if (/cat\s*1(?:\s|$|row)/i.test(text) && !/cat\s*1\d/i.test(text)) {
        return 'cat-1';
      } else if (/cat\s*2/i.test(text)) {
        return 'cat-2';
      }
      return null;
    }

    applyCardCategoryColor(card) {
      const cardText = card.textContent;
      const category = this.detectCardCategory(cardText);

      if (category && CATEGORIES[category]) {
        const color = CATEGORIES[category].color;
        const header = card.querySelector('.category-header');
        if (header) {
          header.style.backgroundColor = color;
          header.style.color = this.getContrastColor(color);
        }
      }
    }

    getContrastColor(hexColor) {
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
    }

    showAllTickets() {
      if (!this.ticketsPanel) return;

      const ticketCards = this.getOriginalTicketCards();
      if (ticketCards.length === 0) {
        this.ticketsPanel.innerHTML = `
          <div class="seating-map-tickets__empty">
            <div class="seating-map-tickets__empty-icon">\uD83C\uDF9F\uFE0F</div>
            <div class="seating-map-tickets__empty-text">No tickets available</div>
          </div>
        `;
        return;
      }

      this.ticketsPanel.innerHTML = '';
      ticketCards.forEach(card => {
        const clone = card.cloneNode(true);
        clone.classList.remove('highlight', 'dimmed');
        this.applyCardCategoryColor(clone);
        this.ticketsPanel.appendChild(clone);
      });

      this.bindTicketEvents();
      this.applySidebarSort();
      this.updatePriceLabelVisibility(null);
    }

    showCategoryTickets(category) {
      if (!this.ticketsPanel) return;

      const ticketCards = this.getOriginalTicketCards();
      const matchingCards = [];

      ticketCards.forEach(card => {
        const cardText = card.textContent.toLowerCase();
        if (this.matchCategory(cardText, category)) {
          matchingCards.push(card);
        }
      });

      if (matchingCards.length === 0) {
        const categoryData = CATEGORIES[category];
        this.ticketsPanel.innerHTML = `
          <div class="seating-map-tickets__empty">
            <div class="seating-map-tickets__empty-icon">\uD83C\uDF9F\uFE0F</div>
            <div class="seating-map-tickets__empty-text">No ${categoryData?.name || category} tickets available</div>
          </div>
        `;
        return;
      }

      this.ticketsPanel.innerHTML = '';
      matchingCards.forEach(card => {
        const clone = card.cloneNode(true);
        clone.classList.add('highlight');
        clone.classList.remove('dimmed');
        this.applyCardCategoryColor(clone);
        this.ticketsPanel.appendChild(clone);
      });

      this.bindTicketEvents();
      this.applySidebarSort();
      this.updatePriceLabelVisibility(category);
    }

    setupSortControls() {
      const select = document.getElementById('sidebar-sort-select');
      if (!select) return;

      select.addEventListener('change', () => {
        this.applySidebarSort();
      });
    }

    applySidebarSort() {
      if (!this.ticketsPanel) return;
      const select = document.getElementById('sidebar-sort-select');
      if (!select) return;

      const value = select.value;
      const cards = Array.from(this.ticketsPanel.querySelectorAll('.ticket-variant-card'));
      if (cards.length === 0) return;

      if (value === 'price-asc') {
        cards.sort((a, b) => (parseFloat(a.dataset.price) || 0) - (parseFloat(b.dataset.price) || 0));
      } else if (value === 'price-desc') {
        cards.sort((a, b) => (parseFloat(b.dataset.price) || 0) - (parseFloat(a.dataset.price) || 0));
      } else {
        cards.sort((a, b) => (parseInt(a.dataset.sortOrder) || 0) - (parseInt(b.dataset.sortOrder) || 0));
      }

      cards.forEach(card => this.ticketsPanel.appendChild(card));
    }

    // "From $X" pill on every purchasable category+tribune group so all four
    // sides of each available ring carry a price signal
    renderPriceLabels() {
      if (!this.map) return;

      const svgNS = 'http://www.w3.org/2000/svg';
      const existingGroup = document.getElementById('price-labels-group');
      if (existingGroup) existingGroup.remove();

      const group = document.createElementNS(svgNS, 'g');
      group.id = 'price-labels-group';
      group.setAttribute('pointer-events', 'none');

      // Group polygons by category+tribune, keep largest polygon for placement
      const tribuneGroups = {};
      if (this.sections) {
        this.sections.forEach(section => {
          const cat = section.dataset.category;
          const range = this.priceRanges[cat];
          if (!range || range.count === 0) return; // sold out — greyed, no pill
          const tribune = section.dataset.tribune || '';
          let bbox;
          try {
            bbox = section.getBBox();
          } catch (e) { return; }
          const area = bbox.width * bbox.height;
          const key = cat + '|' + tribune;
          if (!tribuneGroups[key] || area > tribuneGroups[key].area) {
            tribuneGroups[key] = { cat, bbox, area };
          }
        });
      }

      Object.values(tribuneGroups).forEach(({ cat, bbox }) => {
        const range = this.priceRanges[cat];
        const labelText = `From ${formatMapPrice(range.min)}`;
        const cx = bbox.x + bbox.width / 2;
        const cy = bbox.y + bbox.height / 2;

        const textWidth = labelText.length * 8 + 18;
        const pillHeight = 24;
        const rect = document.createElementNS(svgNS, 'rect');
        rect.setAttribute('x', cx - textWidth / 2);
        rect.setAttribute('y', cy - pillHeight / 2);
        rect.setAttribute('width', textWidth);
        rect.setAttribute('height', pillHeight);
        rect.setAttribute('rx', pillHeight / 2);
        rect.setAttribute('fill', 'rgba(0, 0, 0, 0.75)');
        rect.setAttribute('stroke', 'rgba(255, 255, 255, 0.3)');
        rect.setAttribute('stroke-width', '1');
        rect.setAttribute('pointer-events', 'none');
        rect.classList.add('price-label-bg');
        group.appendChild(rect);

        const text = document.createElementNS(svgNS, 'text');
        text.setAttribute('x', cx);
        text.setAttribute('y', cy + 1);
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('dominant-baseline', 'middle');
        text.setAttribute('pointer-events', 'none');
        text.classList.add('price-label');
        text.textContent = labelText;
        group.appendChild(text);
      });

      this.map.appendChild(group);
    }

    updatePriceLabelVisibility(activeCategory) {
      const group = document.getElementById('price-labels-group');
      if (!group) return;
      group.style.opacity = activeCategory ? '0' : '1';
    }

    bindTicketEvents() {
      if (!this.ticketsPanel) return;

      const cards = this.ticketsPanel.querySelectorAll('.ticket-variant-card');
      cards.forEach(card => {
        card.addEventListener('mouseenter', () => {
          const category = this.detectCardCategory(card.textContent);
          if (category) {
            this.sections.forEach(section => {
              section.classList.remove('active', 'dimmed');
              if (section.dataset.category === category) {
                section.classList.add('active');
              } else {
                section.classList.add('dimmed');
              }
            });
          }
        });

        card.addEventListener('mouseleave', () => {
          if (this.activeCategory) {
            this.sections.forEach(section => {
              section.classList.remove('active', 'dimmed');
              if (section.dataset.category === this.activeCategory) {
                section.classList.add('active');
              } else {
                section.classList.add('dimmed');
              }
            });
          } else {
            this.sections.forEach(section => {
              section.classList.remove('active', 'dimmed');
            });
          }
        });
      });

      if (this._ticketPanelClickHandler) {
        this.ticketsPanel.removeEventListener('click', this._ticketPanelClickHandler, true);
      }

      this._ticketPanelClickHandler = async (e) => {
        const target = e.target.closest('button');
        if (!target) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        const card = target.closest('.ticket-variant-card');
        if (!card) return;

        const qtyInput = card.querySelector('.qty-input');
        const getQuantity = () => parseInt(qtyInput?.value) || 1;
        const maxQty = parseInt(qtyInput?.max) || 10;

        const updateSubtotal = () => {
          const quantity = getQuantity();
          const priceElement = card.querySelector('.price-value');
          const subtotalValue = card.querySelector('[data-subtotal-value]');
          const subtotalQty = card.querySelector('.subtotal-qty');
          const subtotalRow = card.querySelector('.subtotal-row');

          if (priceElement && subtotalValue && subtotalQty) {
            let unitPrice = parseFloat(priceElement.dataset.unitPrice);
            if (isNaN(unitPrice)) {
              unitPrice = parseFloat(priceElement.textContent.replace(/[^0-9.]/g, ''));
            }

            const subtotal = unitPrice * quantity;

            if (subtotalRow) {
              subtotalRow.classList.add('updating');
              setTimeout(() => subtotalRow.classList.remove('updating'), 200);
            }

            subtotalValue.textContent = '$' + subtotal.toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            });
            subtotalQty.textContent = `(${quantity} ticket${quantity !== 1 ? 's' : ''})`;
          }
        };

        // Handle quantity decrease
        if (target.classList.contains('qty-decrease') || target.classList.contains('minus')) {
          const currentQty = getQuantity();
          if (currentQty > 1 && qtyInput) {
            qtyInput.value = currentQty - 1;
            updateSubtotal();
          }
          return;
        }

        // Handle quantity increase
        if (target.classList.contains('qty-increase') || target.classList.contains('plus')) {
          const currentQty = getQuantity();
          if (currentQty < maxQty && qtyInput) {
            qtyInput.value = currentQty + 1;
            updateSubtotal();
          }
          return;
        }

        // Handle Add to Cart
        if (target.classList.contains('add-to-cart-btn')) {
          const variantId = target.dataset.variantId;
          const quantity = getQuantity();

          if (!variantId || variantId === 'undefined' || variantId === '') {
            alert('Unable to add to cart: Missing product information. Please refresh the page and try again.');
            return;
          }

          // Validate quantity against max inventory
          if (quantity > maxQty) {
            alert(`Only ${maxQty} tickets available. Please reduce your quantity.`);
            if (qtyInput) qtyInput.value = maxQty;
            return;
          }

          const originalText = target.innerHTML;
          target.disabled = true;
          target.innerHTML = '<span>Adding...</span>';

          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                items: [{
                  id: parseInt(variantId, 10),
                  quantity: quantity
                }]
              })
            });

            const responseData = await response.json().catch(() => null);

            if (response.ok) {
              target.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg> Added!';

              if (window.innerWidth <= 768) {
                window.location.href = '/cart';
                return;
              }

              try {
                const sectionsResponse = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-drawer`);
                const sectionsData = await sectionsResponse.json();

                const cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer && sectionsData['cart-drawer']) {
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = sectionsData['cart-drawer'];
                  const newDrawerContent = tempDiv.querySelector('cart-drawer');
                  if (newDrawerContent) {
                    cartDrawer.innerHTML = newDrawerContent.innerHTML;
                  }

                  await new Promise(resolve => setTimeout(resolve, 50));

                  if (typeof cartDrawer.show === 'function') {
                    cartDrawer.show();
                  } else if (typeof cartDrawer.open === 'function') {
                    cartDrawer.open();
                  } else {
                    cartDrawer.setAttribute('open', '');
                    cartDrawer.classList.add('is-open');
                  }
                }

                const cartResponse = await fetch('/cart.js');
                const cartData = await cartResponse.json();
                cartData.sections = sectionsData;
                document.dispatchEvent(new CustomEvent('variant:add', {
                  bubbles: true,
                  detail: { items: [], cart: cartData }
                }));
              } catch (drawerError) {
                console.warn('Cart drawer open failed:', drawerError);
                window.location.href = '/cart';
              }

              setTimeout(() => {
                target.innerHTML = originalText;
                target.disabled = false;
                if (qtyInput) qtyInput.value = parseInt(qtyInput.min) || 1;
              }, 2000);
            } else {
              const errorMessage = responseData?.description || responseData?.message || 'Failed to add to cart';
              throw new Error(errorMessage);
            }
          } catch (error) {
            console.error('Error adding to cart:', error);
            var errMsg = error.message || 'Failed to add to cart';
            var btnLabel = errMsg.toLowerCase().includes('maximum quantity') ? 'Max Qty in Cart' : 'Error - Try Again';
            target.innerHTML = btnLabel;
            target.title = errMsg;

            setTimeout(() => {
              target.innerHTML = originalText;
              target.disabled = false;
            }, 2000);
          }
        }

        // Handle Buy Now
        if (target.classList.contains('buy-now-btn')) {
          const variantId = target.dataset.variantId;
          const quantity = getQuantity();

          if (!variantId || variantId === 'undefined' || variantId === '') {
            alert('Unable to proceed: Missing product information. Please refresh the page and try again.');
            return;
          }

          // Validate quantity against max inventory
          if (quantity > maxQty) {
            alert(`Only ${maxQty} tickets available. Please reduce your quantity.`);
            if (qtyInput) qtyInput.value = maxQty;
            return;
          }

          const originalText = target.innerHTML;
          target.disabled = true;
          target.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" class="animate-spin"><path d="M12 2v4m0 12v4m10-10h-4M6 12H2"/></svg> Processing...';

          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                items: [{
                  id: parseInt(variantId, 10),
                  quantity: quantity
                }]
              })
            });

            if (response.ok) {
              target.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg> Redirecting...';
              window.location.href = '/checkout';
            } else {
              const responseData = await response.json().catch(() => null);
              const errorMessage = responseData?.description || responseData?.message || 'Failed to add to cart';
              throw new Error(errorMessage);
            }
          } catch (error) {
            console.error('[BuyNow] Error:', error);
            var errMsg = error.message || 'Failed to add to cart';
            var btnLabel = errMsg.toLowerCase().includes('maximum quantity') ? 'Max Qty in Cart' : 'Error - Try Again';
            target.innerHTML = btnLabel;
            target.title = errMsg;
            setTimeout(() => {
              target.innerHTML = originalText;
              target.disabled = false;
            }, 2000);
          }
        }
      };

      this.ticketsPanel.addEventListener('click', this._ticketPanelClickHandler, true);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new SeatingMapLenglen());
  } else {
    new SeatingMapLenglen();
  }
})();
